<template>
  <div class="page">
    <Modal
      justifyContent="flex-start"
      textAlign="flex-start"
      width="750px"
      :height="isMobile ? '80vh' : '697px'"
      :show="fundraiserStore.createFundraiser || fundraiserStore.editFundraiser || fundraiserStore.success"
      @close="closeModal"
      data-test="fundraiser-modal"
    >
      <template #header>
        <div class="new-fundraiser-header">
          <div class="new-fundraiser-title">
            {{
              currentStep != 6 && !fundraiserStore.editFundraiser
                ? 'New Fundraiser'
                : currentStep != 6 && fundraiserStore.editFundraiser
                ? 'Edit Fundraiser'
                : 'Success'
            }}
          </div>
          <div
            class="current-step"
            v-if="currentStep < 5"
          >
            Step {{ currentStep + '/' + 4 }}
          </div>
        </div></template
      >

      <div
        class="modal-body-container"
        :style="{ width: isMobile ? '370px' : '700px' }"
      >
        <component :is="currentStepComponent" />
      </div>

      <template #footer>
        <Button
          v-if="currentStep < 6 || fundraiserStore.saveError"
          variant="link"
          label="Back"
          :style="`color: ${donorStore.mainColor}`"
          :class="{ invisible: currentStep === 1 }"
          @click="prevStep"
          :disabled="currentStep === 1"
          data-test="back-button"
        ></Button>
        <div class="flex items-center">
          <div
            v-if="Object.keys(fundraiserStore.errors).length"
            class="p-1 w-6 h-6 text-xs bg-[red] rounded-full text-white text-center mr-4"
          >
            {{ Object.keys(fundraiserStore.errors).length }}
          </div>
          <Button
            :style="`background-color: ${mainColor}; border-color: ${mainColor}; border-radius: 4px;color: ${getContrastYIQ(
              donorStore.mainColor,
            )}`"
            label="Next"
            custom-font="false"
            @click="nextStep"
            :disabled="fundraiserStore.isLoading"
            data-test="next-button"
            v-if="currentStep < 4"
          ></Button>
          <Button
            :style="`background-color: ${mainColor}; border-color: ${mainColor}; border-radius: 4px;color: ${getContrastYIQ(
              donorStore.mainColor,
            )}`"
            label="Publish"
            @click="nextStep"
            :disabled="fundraiserStore.isLoading"
            data-test="next-button"
            v-if="currentStep === 4"
          ></Button>
          <div v-if="currentStep === 5"></div>
          <Button
            :style="`background-color: ${mainColor}; border-color: ${mainColor}; border-radius: 4px;color: ${getContrastYIQ(
              donorStore.mainColor,
            )}`"
            label="Finish"
            custom-font="false"
            @click="closeModal"
            :disabled="fundraiserStore.isLoading"
            data-test="next-button"
            v-if="currentStep === 6"
          ></Button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeMount } from 'vue';
import { useRouter } from 'vue-router';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import Modal from '@/components/Modal.vue';
import Step1 from '@/components/fundraisers/creationSteps/Step1.vue';
import Step2 from '@/components/fundraisers/creationSteps/Step2.vue';
import Step3 from '@/components/fundraisers/creationSteps/Step3.vue';
import Step4 from '@/components/fundraisers/creationSteps/Step4.vue';
import Step5 from '@/components/fundraisers/creationSteps/Step5.vue';
import Step6 from '@/components/fundraisers/creationSteps/Step6.vue';
import useIsMobile from '@/composables/useIsMobile.js';

const { isMobile } = useIsMobile();
const router = useRouter();

const props = defineProps({
  step: {
    type: Number,
    default: 1,
  },
});

const donorStore = useDonorStore();
const fundraiserStore = useFundraiserStore();
const mainColorStatic = donorStore.mainColor;
const mainColor = computed(() => (fundraiserStore.isLoading ? '#aaa' : donorStore.mainColor));

const currentStep = ref(props.step);
const currentStepComponent = computed(() => {
  switch (currentStep.value) {
    case 1:
      return Step1;
    case 2:
      return Step2;
    case 3:
      return Step3;
    case 4:
      return Step4;
    case 5:
      return Step5;
    case 6:
      return Step6;
    default:
      return Step1;
  }
});

const nextStep = async () => {
  if (currentStep.value === 1) {
    const isValid = await validateStep1();
    if (!isValid) return;
  }
  if (currentStep.value === 2 && !validateStep2()) {
    return;
  }
  if (currentStep.value === 3 && !validateStep3()) {
    return;
  }
  if (currentStep.value === 4) {
    let result;
    fundraiserStore.saveError = null;
    currentStep.value++;

    if (fundraiserStore.editFundraiser) {
      await fundraiserStore.updateFundraiser();
    } else {
      await fundraiserStore.publishFundraiser();
    }

    if (!fundraiserStore.saveError) {
      currentStep.value++;
    }
  }
  if (currentStep.value === 7) {
    closeModal();
    return;
  }
  if (currentStep.value < 5) {
    currentStep.value++;
  }
};
const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const closeModal = () => {
  if (fundraiserStore.createFundraiser) {
    fundraiserStore.switchToFundraiserDetailsPage();
  } else {
    // Remove /edit from URL
    router.replace(router.currentRoute.value.path.replace('/edit', ''));
    fundraiserStore.createFundraiser = false;
    donorStore.fetchFundraisers();
  }
  fundraiserStore.resetData();
  currentStep.value = 1;
};

const validateStep1 = async () => {
  fundraiserStore.errors = {}; // Clear previous errors
  if (!fundraiserStore.editFundraiser && (!fundraiserStore.benefittingOrg || fundraiserStore.benefittingOrg == '')) {
    fundraiserStore.errors.benefittingOrg = 'Benefitting Organization is required.';
  }
  if (!fundraiserStore.fundraiserTitle) {
    fundraiserStore.errors.fundraiserTitle = 'Fundraiser Title is required.';
  }
  if (!fundraiserStore.urlLink) {
    fundraiserStore.errors.urlLink = 'Custom URL is required.';
  }
  if (!fundraiserStore.goal || isNaN(fundraiserStore.goal) || Number(fundraiserStore.goal) <= 0) {
    fundraiserStore.errors.goal = 'A valid Fundraiser Goal is required.';
  }

  if (fundraiserStore.urlLink) {
    const invalidCharactersRegex = /[^a-zA-Z0-9-]/;
    if (invalidCharactersRegex.test(fundraiserStore.urlLink)) {
      fundraiserStore.errors.urlLink = 'Custom URL contains invalid characters, you can only use "-" between words.';
    } else if (fundraiserStore.urlLink.length > 50) {
      fundraiserStore.errors.urlLink = 'Custom URL is too long.';
    } else if (fundraiserStore.urlLink.length < 5) {
      fundraiserStore.errors.urlLink = 'Custom URL must be at least 5 characters long.';
    }
  }

  // If we have any errors at this point, skip the custom URL check and stop here.
  if (Object.keys(fundraiserStore.errors).length > 0) {
    return false;
  }

  if (fundraiserStore.urlLink) {
    if (!fundraiserStore.editFundraiser) {
      let duplicateUrl = await fundraiserStore.checkFundraiserUrl();
      if (duplicateUrl) {
        fundraiserStore.errors.urlLink = 'Custom URL is already taken.';
      }
    }
  }

  return Object.keys(fundraiserStore.errors).length === 0;
};
const validateStep2 = () => {
  fundraiserStore.errors = {}; // Clear previous errors
  if (!fundraiserStore.story) {
    fundraiserStore.errors.story = 'Fundraiser Story is required.';
  }
  if (fundraiserStore.story.length > 1000) {
    fundraiserStore.errors.story = 'Fundraiser Story is too long.';
  }

  return Object.keys(fundraiserStore.errors).length === 0;
};
const validateStep3 = () => {
  fundraiserStore.errors = {}; // Clear previous errors
  if (!fundraiserStore.image && !fundraiserStore.heroLink) {
    fundraiserStore.errors.image = 'Fundraiser Image is required.';
  }
  return Object.keys(fundraiserStore.errors).length === 0;
};

onBeforeMount(async () => {
  await fundraiserStore.bringAllOrganizations();
});
</script>
<style lang="scss">
.new-fundraiser-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 600px;
}

.new-fundraiser-title {
  font-size: 2.75rem;
  font-weight: bold;
  color: var(--p-primary-500);
}

.modal-body-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  .overflow-y-scroll {
    height: 390px;

    @media screen and (max-width: 768px) {
      height: calc(80vh - 220px);
    }
  }
}

.current-step {
  font-size: 1rem;
  font-weight: 400;
}

.scrollbar::-webkit-scrollbar {
  width: 10px;
  height: 20px;
}

.scrollbar::-webkit-scrollbar-track {
  background: #fff;
}

.scrollbar::-webkit-scrollbar-thumb {
  background: var(--p-primary-500);
  border: 3px solid #fff;
}

.scrollbar::-webkit-scrollbar-thumb:hover {
  background: var(--p-primary-500);
}

@media screen and (max-width: 768px) {
  .new-fundraiser-header {
    flex-direction: column;
    position: static;
  }
  .new-fundraiser-title {
    font-size: 1.875rem;
  }
}
</style>
